import React, { useCallback, useMemo } from 'react';
import { cn, ToolButton, ValueInputField } from '@crabs-ai/ds-react';
import { ColorPickerField } from '../components/color-picker-field';
import {
  BlurOn,
  Remove,
  Visibility,
  VisibilityOffFill,
} from '@crabs-ai/web-icons';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import {
  TextItem,
  TextShadow,
  defaultTextShadow,
} from '../../items/text/text-item-type';
import { CaptionsItem } from '../../items/captions/captions-item-type';
import { SolidItem } from '../../items/solid/solid-item-type';
import { InspectorSubLabel } from '../components/inspector-label';
import { NumberControlUpdateHandler } from './number-controls';
import { useTranslations } from 'next-intl';
import { useSelectionValues } from '../utils/use-selection-values';

type ShadowControlsProps = {
  itemId: string;
  shadow?: TextShadow;
  isEyeOff?: boolean;
  onToggleEyeVisibility?: () => void;
  onDeactivate?: () => void;
};

const ShadowControls: React.FC<ShadowControlsProps> = ({
  itemId,
  shadow,
  isEyeOff = false,
  onToggleEyeVisibility,
  onDeactivate,
}) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);

  const { items: shadowItems } = useSelectionValues<
    TextItem | CaptionsItem | SolidItem,
    TextShadow | undefined
  >(itemId, (item) => (item as TextItem).shadow);

  const normalizedShadows = useMemo(() => {
    if (shadowItems.length === 0) {
      return shadow
        ? [
            {
              ...defaultTextShadow,
              ...shadow,
            },
          ]
        : [];
    }

    return shadowItems.map((current) => {
      const currentShadow = (current.shadow ?? {}) as Partial<TextShadow>;
      return {
        ...defaultTextShadow,
        ...currentShadow,
      };
    });
  }, [shadowItems, shadow]);

  const shadowValue = useMemo<TextShadow>(() => {
    if (normalizedShadows[0]) {
      return {
        ...normalizedShadows[0],
      };
    }

    return {
      ...defaultTextShadow,
      ...(shadow ?? {}),
    };
  }, [normalizedShadows, shadow]);

  const isDefaultShadow = useMemo(() => {
    if (normalizedShadows.length === 0) {
      return (
        defaultTextShadow.color === shadowValue.color &&
        Math.abs(defaultTextShadow.opacity - shadowValue.opacity) < 0.0001 &&
        defaultTextShadow.visible === shadowValue.visible &&
        defaultTextShadow.offsetX === shadowValue.offsetX &&
        defaultTextShadow.offsetY === shadowValue.offsetY &&
        defaultTextShadow.blur === shadowValue.blur
      );
    }

    return normalizedShadows.every((current) => {
      return (
        current.color === defaultTextShadow.color &&
        Math.abs(current.opacity - defaultTextShadow.opacity) < 0.0001 &&
        current.visible === defaultTextShadow.visible &&
        current.offsetX === defaultTextShadow.offsetX &&
        current.offsetY === defaultTextShadow.offsetY &&
        current.blur === defaultTextShadow.blur
      );
    });
  }, [normalizedShadows, shadowValue]);

  const isColorMixed = useMemo(() => {
    if (normalizedShadows.length <= 1) {
      return false;
    }

    const first = normalizedShadows[0]?.color ?? defaultTextShadow.color;
    return normalizedShadows.some((current) => current.color !== first);
  }, [normalizedShadows]);

  const isOpacityMixed = useMemo(() => {
    if (normalizedShadows.length <= 1) {
      return false;
    }

    const first = normalizedShadows[0]?.opacity ?? defaultTextShadow.opacity;
    return normalizedShadows.some(
      (current) => Math.abs(current.opacity - first) > 0.0001,
    );
  }, [normalizedShadows]);

  const isOffsetXMixed = useMemo(() => {
    if (normalizedShadows.length <= 1) {
      return false;
    }

    const first = normalizedShadows[0]?.offsetX ?? defaultTextShadow.offsetX;
    return normalizedShadows.some((current) => current.offsetX !== first);
  }, [normalizedShadows]);

  const isOffsetYMixed = useMemo(() => {
    if (normalizedShadows.length <= 1) {
      return false;
    }

    const first = normalizedShadows[0]?.offsetY ?? defaultTextShadow.offsetY;
    return normalizedShadows.some((current) => current.offsetY !== first);
  }, [normalizedShadows]);

  const isBlurMixed = useMemo(() => {
    if (normalizedShadows.length <= 1) {
      return false;
    }

    const first = normalizedShadows[0]?.blur ?? defaultTextShadow.blur;
    return normalizedShadows.some((current) => current.blur !== first);
  }, [normalizedShadows]);

  const updateShadow = useCallback(
    (
      updater: (current: TextShadow) => TextShadow,
      commitToUndoStack: boolean,
    ) => {
      setState({
        update: (state) =>
          changeItemsWithSelection(state, itemId, targetIds, (item) => {
            if (
              item.type !== 'text' &&
              item.type !== 'captions' &&
              item.type !== 'solid'
            ) {
              return item;
            }

            const currentShadow: TextShadow = {
              ...defaultTextShadow,
              ...((item as TextItem | SolidItem).shadow ?? {}),
            };
            const nextShadow = updater(currentShadow);

            return {
              ...(item as TextItem | SolidItem),
              shadow: { ...nextShadow },
            };
          }),
        commitToUndoStack,
      });
    },
    [itemId, setState, targetIds],
  );

  const handleColorChange = useCallback(
    (color: string, commitToUndoStack = true) => {
      const normalized =
        color === 'transparent'
          ? color
          : color.startsWith('#')
            ? color
            : `#${color}`;
      updateShadow(
        (current) => ({
          ...current,
          color: normalized,
        }),
        commitToUndoStack,
      );
    },
    [updateShadow],
  );

  const handleOpacityChange = useCallback(
    (newOpacity: number, commitToUndoStack = true) => {
      updateShadow(
        (current) => ({ ...current, opacity: newOpacity }),
        commitToUndoStack,
      );
    },
    [updateShadow],
  );

  const handleOffsetXChange = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      updateShadow(
        (current) => ({
          ...current,
          offsetX: num,
        }),
        commitToUndoStack,
      );
    },
    [updateShadow],
  );

  const handleOffsetYChange = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      updateShadow(
        (current) => ({
          ...current,
          offsetY: num,
        }),
        commitToUndoStack,
      );
    },
    [updateShadow],
  );

  const handleBlurChange = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      const clamped = Math.max(0, num);
      updateShadow(
        (current) => ({
          ...current,
          blur: clamped,
        }),
        commitToUndoStack,
      );
    },
    [updateShadow],
  );

  const handleToggleVisible = useCallback(() => {
    onToggleEyeVisibility?.();
  }, [onToggleEyeVisibility]);

  const handleReset = useCallback(() => {
    updateShadow(() => ({ ...defaultTextShadow }), true);
    onDeactivate?.();
  }, [updateShadow, onDeactivate]);

  const isInvisible = isEyeOff;

  return (
    <div className="flex flex-col gap-3xs">
      <div className="flex items-center gap-4xs">
        <div
          className={cn(
            'flex flex-1 items-center gap-4xs',
            isInvisible && 'pointer-events-none opacity-40',
          )}
        >
          <ColorPickerField
            value={shadowValue.color}
            onChange={(color) => handleColorChange(color, false)}
            onChangeEnd={(color) => handleColorChange(color, true)}
            accessibilityLabel="Shadow color"
            hasMixedValue={isColorMixed}
            opacity={shadowValue.opacity}
            onOpacityChange={(o) => handleOpacityChange(o, false)}
            onOpacityChangeEnd={(o) => handleOpacityChange(o, true)}
            isOpacityMixed={isOpacityMixed}
            className="flex flex-1 items-center gap-4xs"
          />
        </div>
        <ToolButton
          aria-label="Toggle shadow visibility"
          onClick={handleToggleVisible}
          isShowTooltip
          tooltipNode={!isEyeOff ? '속성 끄기' : '속성 켜기'}
          state={isEyeOff ? 'selected' : 'default'}
          isTransparent={false}
          className="h-[36px] w-[36px]"
        >
          {!isEyeOff ? (
            <Visibility className="size-4" />
          ) : (
            <VisibilityOffFill className="size-4" />
          )}
        </ToolButton>
        <ToolButton
          aria-label="Reset shadow"
          onClick={handleReset}
          disabled={isDefaultShadow}
          isShowTooltip
          tooltipNode="속성 삭제"
          className={cn(
            'h-[36px] w-[36px] text-sys-cont-neutral-default',
            isDefaultShadow && 'opacity-40',
          )}
        >
          <Remove className="size-4" />
        </ToolButton>
      </div>

      <div
        className={cn(
          'flex flex-col gap-3xs',
          isInvisible && 'pointer-events-none opacity-40',
        )}
      >
        <div className="flex flex-col gap-2xs">
          <InspectorSubLabel>
            {t('Editor.inspector.position')}
          </InspectorSubLabel>
          <div className="flex items-center gap-4xs">
            <div className="min-w-0 flex-1">
              <ValueInputField
                isUnit="px"
                isShowLeadingNode={true}
                leadingNodeType="text"
                leadingNodeValue="X"
                setValue={handleOffsetXChange}
                value={shadowValue.offsetX}
                min={undefined}
                max={undefined}
                step={1}
                accessibilityLabel="Shadow offset X"
                hasMixedValue={isOffsetXMixed}
              />
            </div>
            <div className="min-w-0 flex-1">
              <ValueInputField
                isUnit="px"
                isShowLeadingNode={true}
                leadingNodeType="text"
                leadingNodeValue="Y"
                setValue={handleOffsetYChange}
                value={shadowValue.offsetY}
                min={undefined}
                max={undefined}
                step={1}
                accessibilityLabel="Shadow offset Y"
                hasMixedValue={isOffsetYMixed}
              />
            </div>
          </div>
        </div>

        <div className="flex w-1/2 flex-col gap-2xs">
          <ValueInputField
            isShowLabel={true}
            label={t('Editor.inspector.blur-amount')}
            isShowLeadingNode={true}
            leadingNodeType="icon"
            leadingNodeValue={
              <BlurOn
                className={'size-[20px] [&_path]:fill-sys-cont-neutral-default'}
              />
            }
            setValue={handleBlurChange}
            value={shadowValue.blur}
            min={0}
            max={200}
            step={1}
            accessibilityLabel="Shadow blur"
            hasMixedValue={isBlurMixed}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};

export default ShadowControls;
